import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import axios from "axios";
import ForkliftCard from "@/components/ForkliftCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    capacity_min: "", capacity_max: "",
    engine_type: "any", mast_stages: "",
    tire_type: "any", drive: "any",
    condition: "any", budget_max: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Please fill in the client name.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        email: form.email || `${form.name.toLowerCase().replace(/\s+/g, ".")}@noemail.local`,
        phone: form.phone,
        capacity_min: form.capacity_min ? parseInt(form.capacity_min) : null,
        capacity_max: form.capacity_max ? parseInt(form.capacity_max) : null,
        engine_type: form.engine_type !== "any" ? form.engine_type : null,
        mast_stages: form.mast_stages ? parseInt(form.mast_stages) : null,
        tire_type: form.tire_type !== "any" ? form.tire_type : null,
        drive: form.drive !== "any" ? form.drive : null,
        condition: form.condition !== "any" ? form.condition : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        notes: form.notes,
      };
      const { data } = await axios.post(`${API}/customer-requests`, body);
      setResult(data);
    } catch (err) {
      toast.error("Error submitting request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(result.request.id);
    toast.success("Request ID copied!");
  };

  if (result) {
    return (
      <div data-testid="request-success" className="pt-24 pb-16 min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <CheckCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-gray-900">{t.request.success_title}</h1>
            <p className="mt-2 text-base text-gray-600">{t.request.success_text}</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-sm p-6 mb-8">
            <p className="text-sm font-semibold text-gray-700 mb-2">{t.request.your_request_id}</p>
            <div className="flex items-center gap-2">
              <code data-testid="request-id-display" className="font-mono text-sm bg-white border border-gray-200 px-3 py-2 rounded-sm flex-1">
                {result.request.id}
              </code>
              <button data-testid="copy-request-id" onClick={copyId} className="p-2 hover:bg-gray-200 rounded-sm">
                <Copy className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              {result.match_count} {t.request.matches_found}
            </p>
          </div>

          {result.matches.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                {t.request_status.matches}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.matches.map((f) => (
                  <ForkliftCard key={f.id} forklift={f} />
                ))}
              </div>
            </div>
          )}

          {result.matches.length === 0 && (
            <p className="text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-8">
              {t.request.no_matches}
            </p>
          )}

          <div className="mt-8 flex gap-4 justify-center">
            <Link
              to={`/check-request?id=${result.request.id}`}
              data-testid="view-matches-link"
              className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-5 py-2 rounded-sm text-sm"
            >
              {t.request.view_matches}
            </Link>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-900 font-medium hover:bg-gray-200 transition-colors px-5 py-2 rounded-sm text-sm border border-gray-200"
            >
              {t.nav.inventory}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="request-page" className="pt-24 pb-16 min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-700 mb-2">
            {t.hero.overline}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-gray-900">
            {t.request.title}
          </h1>
          <p className="mt-2 text-base text-gray-700">{t.request.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="request-form">
          {/* Contact Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-6">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
              {t.request.name.split(" ")[0]}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.name} *</label>
                <Input data-testid="input-name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} required className="bg-white" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.email}</label>
                <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="bg-white" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.phone}</label>
                <Input data-testid="input-phone" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="bg-white" />
              </div>
            </div>
          </div>

          {/* Forklift Criteria */}
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-6">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
              {t.inventory.title}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.condition}</label>
                <Select value={form.condition} onValueChange={(v) => handleChange("condition", v)}>
                  <SelectTrigger data-testid="select-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t.inventory.condition_any}</SelectItem>
                    <SelectItem value="new">{t.inventory.condition_new}</SelectItem>
                    <SelectItem value="used">{t.inventory.condition_used}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.engine_type}</label>
                <Select value={form.engine_type} onValueChange={(v) => handleChange("engine_type", v)}>
                  <SelectTrigger data-testid="select-engine"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t.inventory.engine_any}</SelectItem>
                    <SelectItem value="propane">{t.inventory.engine_propane}</SelectItem>
                    <SelectItem value="diesel">{t.inventory.engine_diesel}</SelectItem>
                    <SelectItem value="electric">{t.inventory.engine_electric}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.mast_stages}</label>
                <Select value={form.mast_stages || "any"} onValueChange={(v) => handleChange("mast_stages", v === "any" ? "" : v)}>
                  <SelectTrigger data-testid="select-mast"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t.inventory.mast_any}</SelectItem>
                    <SelectItem value="2">{t.inventory.mast_2}</SelectItem>
                    <SelectItem value="3">{t.inventory.mast_3}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.tire_type}</label>
                <Select value={form.tire_type} onValueChange={(v) => handleChange("tire_type", v)}>
                  <SelectTrigger data-testid="select-tires"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t.inventory.tire_any}</SelectItem>
                    <SelectItem value="pneumatic">{t.inventory.tire_pneumatic}</SelectItem>
                    <SelectItem value="cushion">{t.inventory.tire_cushion}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.drive}</label>
                <Select value={form.drive} onValueChange={(v) => handleChange("drive", v)}>
                  <SelectTrigger data-testid="select-drive"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t.inventory.drive_any}</SelectItem>
                    <SelectItem value="2wd">{t.inventory.drive_2wd}</SelectItem>
                    <SelectItem value="4wd">{t.inventory.drive_4wd}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.budget}</label>
                <Input data-testid="input-budget" type="number" placeholder="$" value={form.budget_max} onChange={(e) => handleChange("budget_max", e.target.value)} className="bg-white" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.capacity_range} ({t.request.min})</label>
                <Input data-testid="input-capacity-min" type="number" value={form.capacity_min} onChange={(e) => handleChange("capacity_min", e.target.value)} className="bg-white" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.capacity_range} ({t.request.max})</label>
                <Input data-testid="input-capacity-max" type="number" value={form.capacity_max} onChange={(e) => handleChange("capacity_max", e.target.value)} className="bg-white" />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm font-semibold text-gray-700 mb-1 block">{t.request.notes}</label>
              <Textarea data-testid="input-notes" placeholder={t.request.notes_placeholder} value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} className="bg-white" />
            </div>
          </div>

          <button
            data-testid="submit-request-btn"
            type="submit"
            disabled={submitting}
            className="w-full bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-6 py-3 rounded-sm disabled:opacity-50"
          >
            {submitting ? "..." : t.request.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
